"use client"

import type React from "react"
import { useEffect, useRef } from "react"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { trackEvent } from "@/lib/analytics"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import type { SavedPlanAnalyticsContext } from "@/lib/saved-plan-analytics"
import { ResultUpgradeCard } from "./result-upgrade-card"
import { getResultPlanMonth } from "./result-plan-card"

export type ResultSaveModalTrigger = "hero_cta" | "sticky_bar"

/** Control the user actually tapped. `trigger` names the surface; this names
 *  the affordance, which is the dimension the hero pills and the save-by-email
 *  link differ on inside one surface. */
export type ResultSaveModalOpenedVia = "hero_cta" | "email_link" | "sticky_bar"

interface ResultSaveModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which surface opened the modal — funnel dimension on the impression event. */
  trigger: ResultSaveModalTrigger | null
  /** Which control opened the modal. Defaults to the hero CTA. */
  openedVia?: ResultSaveModalOpenedVia
  grassType: string
  confidence?: number
  /**
   * Whether the grass name is settled enough to claim in the title and ask
   * copy. Required, not derived: this modal mounts for moderate-confidence
   * results too, and an unconditional `true` here overclaimed a 66% guess
   * while the on-page digest for the same result correctly hedged.
   */
  grassNameIsConfirmed: boolean
  /**
   * Whether to cite USDA Plant Hardiness backing in the footer. Mirrors the
   * digest, which reserves the proof point for high-confidence results; the
   * modal previously claimed it unconditionally, so the two asks disagreed
   * on the same page at moderate confidence.
   */
  showUsdaBacking: boolean
  entryPath: string
  /** Email-ask wiring, forwarded to the embedded upgrade card (same contract
   *  as ResultCarePlanDigest). */
  email: string
  emailSuggestion: EmailSuggestion | null
  isSubmitting: boolean
  timelineRedirectUrl?: string
  savedPlanContext: SavedPlanAnalyticsContext
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onAcceptSuggestion: () => void
  onDismissSuggestion: () => void
}

/**
 * Save-plan modal opened directly from the hero CTA / mobile sticky bar
 * (2026-07-14 conversion pass). Previously those buttons smooth-scrolled to the
 * `#care-plan-upgrade` digest, forcing a second decision on a second card —
 * PostHog showed the hero CTA converting ~2% of high-confidence viewers and
 * only 4.7% of ask-viewers clicking through. One intent click now lands on one
 * auth decision: Google/Apple pills (account-first) with the quiet email
 * fallback, reusing the digest's embedded ResultUpgradeCard body. The on-page
 * digest card stays — scroll-through users still convert there.
 */
export function ResultSaveModal({
  open,
  onOpenChange,
  trigger,
  openedVia,
  grassType,
  grassNameIsConfirmed,
  showUsdaBacking,
  confidence,
  entryPath,
  email,
  emailSuggestion,
  isSubmitting,
  timelineRedirectUrl,
  savedPlanContext,
  onPrimaryCtaClick,
  onEmailChange,
  onEmailKeyPress,
  onSubmit,
  onAcceptSuggestion,
  onDismissSuggestion,
}: ResultSaveModalProps) {
  const { monthName } = getResultPlanMonth(grassType)
  // A host that opens the modal from the sticky bar already says so through
  // `trigger`, so the affordance is derivable there. Only surfaces with more
  // than one control in one place have to pass `openedVia` themselves.
  const resolvedOpenedVia: ResultSaveModalOpenedVia =
    openedVia ?? (trigger === "sticky_bar" ? "sticky_bar" : "hero_cta")

  // One impression per open (re-fires on re-open — each is a real exposure).
  const wasOpenRef = useRef(false)
  // Close accounting. `result_save_modal_shown` had no counterpart of any kind,
  // so the modal's own outcome was unmeasurable: an open that converted and an
  // open the user closed instantly were indistinguishable. `acted` separates
  // them without the component needing to know the submit result, and
  // `dwell_ms` separates a considered decline from a reflex close.
  const openedAtRef = useRef(0)
  const actedRef = useRef(false)
  if (isSubmitting) actedRef.current = true

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      openedAtRef.current = Date.now()
      actedRef.current = false
      trackEvent("result_save_modal_shown", {
        grass_type: grassType,
        confidence,
        trigger: trigger ?? undefined,
        opened_via: resolvedOpenedVia,
        entry_path: entryPath,
      })
    } else if (!open && wasOpenRef.current) {
      trackEvent("result_save_modal_closed", {
        grass_type: grassType,
        confidence,
        trigger: trigger ?? undefined,
        entry_path: entryPath,
        dwell_ms: Math.round(Date.now() - openedAtRef.current),
        acted: actedRef.current,
      })
    }
    wasOpenRef.current = open
  }, [open, grassType, confidence, trigger, resolvedOpenedVia, entryPath])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl font-extrabold tracking-tight text-gray-900">
            {grassNameIsConfirmed ? `Save my ${grassType} plan` : "Save my lawn plan"}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            This {monthName}&apos;s tasks + your 12-month schedule &middot; Free
          </DialogDescription>
        </DialogHeader>

        <ResultUpgradeCard
          embedded
          savePlanOauth
          askOrder="account_first"
          // Same surface as the on-page identify gate: this modal's email lane
          // already reports email_captured as grass_id_gate, and a *_modal
          // source would misfile it into the generic modal family.
          // result_save_modal_shown still separates the two when needed.
          source="grass_id_gate"
          // Both lanes stay on screen. The reveal link hid the email path
          // behind a second tap, which reads as "accounts only" to anyone who
          // does not want one.
          emailRevealed
          copy={{
            googleLabel: "Continue with Google",
            appleLabel: "Continue with Apple",
            emailDividerLabel: "Email address",
          }}
          email={email}
          emailSuggestion={emailSuggestion}
          isSubmitting={isSubmitting}
          timelineRedirectUrl={timelineRedirectUrl}
          savedPlanContext={savedPlanContext}
          grassName={grassType}
          grassNameIsConfirmed={grassNameIsConfirmed}
          onPrimaryCtaClick={onPrimaryCtaClick}
          onEmailChange={onEmailChange}
          onEmailKeyPress={onEmailKeyPress}
          onSubmit={onSubmit}
          onAcceptSuggestion={onAcceptSuggestion}
          onDismissSuggestion={onDismissSuggestion}
        />

        <p className="text-center text-xs leading-relaxed text-gray-500">
          No credit card. No phone calls. No spam.
          {showUsdaBacking && (
            <>
              {" "}Backed by{" "}
              <span className="font-semibold text-gray-700">USDA Plant Hardiness data</span>.
            </>
          )}
        </p>
      </DialogContent>
    </Dialog>
  )
}
